import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Sparkles, ArrowLeft, X, AlertTriangle } from 'lucide-react'
import { usePlayCreateStore } from '../stores/playCreateStore'
import { CharacterCard } from '../components/CharacterCard'
import { ChatWindow } from '../components/ChatWindow'
import { CharacterImageGenerator } from '../components/CharacterImageGenerator'
import { CharacterProfileModal } from '../components/CharacterProfileModal'
import playCreateStorageService from '../services/playCreateStorageService'
import personalityOptimizationService from '../services/personalityOptimizationService.js'
import toast from 'react-hot-toast'

export const PlayCreateMode = () => {
  const navigate = useNavigate()
  const { characterId } = useParams()
  const { characters, loadCharacters, selectCharacter, currentCharacter, clearOptions } = usePlayCreateStore()
  const [isLoading, setIsLoading] = useState(true)
  const [characterStates, setCharacterStates] = useState({})
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImageGenerator, setShowImageGenerator] = useState(false)
  const [createdCharacter, setCreatedCharacter] = useState(null)
  const [loadingCharacterId, setLoadingCharacterId] = useState(null)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [selectedCharacterForProfile, setSelectedCharacterForProfile] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [characterToDelete, setCharacterToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // 如果没有角色ID且没有创建标记，重定向到我的世界页面的角色标签
  useEffect(() => {
    if (!characterId) {
      // 检查是否有创建角色的标记（从首页或其他地方传递）
      const creationDescription = sessionStorage.getItem('character-creation-description')
      const hasAutoCreate = sessionStorage.getItem('character-auto-create')
      
      // 如果没有创建标记，重定向到我的世界页面
      if (!creationDescription && !hasAutoCreate) {
        navigate('/my-world?tab=characters', { replace: true })
      }
    }
  }, [characterId, navigate])

  // 加载角色列表和状态
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        
        // 检查是否有从首页传递过来的角色创建描述
        const creationDescription = sessionStorage.getItem('character-creation-description')
        if (creationDescription && !characterId) {
          // 如果有描述且没有选中角色，自动打开创建对话框
          sessionStorage.removeItem('character-creation-description')
          setShowCreateModal(true)
          // 将描述传递给创建对话框（需要在CreateCharacterModal组件中处理）
          sessionStorage.setItem('character-initial-description', creationDescription)
        }
        
        // 加载角色档案
        const profiles = await playCreateStorageService.getAllCharacterProfiles()
        
        // 加载每个角色的状态
        const states = {}
        for (const profile of profiles) {
          const state = await playCreateStorageService.getCharacterState(profile.id)
          states[profile.id] = state
        }
        
        setCharacterStates(states)
        // 更新store中的角色列表
        await loadCharacters()
        
        // 如果有characterId参数，选择该角色
        if (characterId) {
          const character = profiles.find(c => c.id === characterId)
          if (character) {
            selectCharacter(character)
          } else {
            // 角色不在列表中，尝试从存储中单独加载
            setLoadingCharacterId(characterId)
          }
        }
      } catch (error) {
        console.error('❌ 加载角色数据失败:', error)
        toast.error('加载角色数据失败')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadData()
  }, [characterId, loadCharacters, selectCharacter, navigate])

  // 处理查看档案
  const handleViewProfile = (character) => {
    setSelectedCharacterForProfile(character)
    setShowProfileModal(true)
  }

  // 处理删除角色
  const handleDeleteClick = (character) => {
    setCharacterToDelete(character)
    setShowDeleteConfirm(true)
  }

  // 确认删除角色
  const handleConfirmDelete = async () => {
    if (!characterToDelete) return

    try {
      setIsDeleting(true)
      toast.loading('正在删除角色...', { id: 'deleting-character' })

      // 完整删除角色及其所有相关数据
      await playCreateStorageService.deleteCharacterCompletely(characterToDelete.id)

      toast.success('角色已删除', { id: 'deleting-character' })
      
      // 重新加载角色列表
      await loadCharacters()
      
      // 关闭对话框
      setShowDeleteConfirm(false)
      setCharacterToDelete(null)
    } catch (error) {
      console.error('❌ 删除角色失败:', error)
      toast.error('删除失败：' + error.message, { id: 'deleting-character' })
    } finally {
      setIsDeleting(false)
    }
  }

  // 取消删除
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
    setCharacterToDelete(null)
  }

  // 如果角色不在列表中，尝试单独加载
  useEffect(() => {
    if (loadingCharacterId && !currentCharacter) {
      const loadCharacter = async () => {
        try {
          const profile = await playCreateStorageService.getCharacterProfile(loadingCharacterId)
          if (profile) {
            selectCharacter(profile)
            setLoadingCharacterId(null)
          } else {
            navigate('/my-world?tab=characters')
          }
        } catch (error) {
          console.error('加载角色失败:', error)
          navigate('/my-world?tab=characters')
        }
      }
      loadCharacter()
    }
  }, [loadingCharacterId, currentCharacter, selectCharacter, navigate])

  // 如果选中了角色，显示聊天窗口
  if (currentCharacter || characterId) {
    const displayCharacter = currentCharacter || characters.find(c => c.id === characterId)
    
    // 如果正在加载角色，显示加载状态
    if (!displayCharacter && loadingCharacterId) {
      return (
        <div className="min-h-screen bg-dark-900 flex items-center justify-center">
          <div className="text-dark-400">加载角色中...</div>
        </div>
      )
    }
    
    if (!displayCharacter) {
      return null
    }
    
    return (
      <div className="min-h-screen bg-dark-900">
        <ChatWindow
          character={displayCharacter}
          onBack={() => {
            clearOptions() // 清除对话选项
            selectCharacter(null)
            // 使用浏览器历史记录返回，而不是导航到玩创界面
            navigate(-1)
          }}
        />
      </div>
    )
  }

  // 角色列表页面
  return (
    <div className="min-h-screen bg-dark-900 relative overflow-hidden">
      {/* 背景粒子效果 */}
      <div id="particles-playcreate" className="absolute inset-0 pointer-events-none" />
      
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* 页面标题 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
              <Sparkles className="text-primary" size={36} />
              玩创模式
            </h1>
            <p className="text-dark-300">与AI角色聊天，创造属于你们的故事</p>
          </div>
          
          {/* 创建角色按钮 */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-secondary hover:from-primary/80 hover:to-secondary/80 text-white font-medium rounded-lg transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/40"
          >
            <Plus size={20} />
            创建角色
          </button>
        </div>

        {/* 角色网格 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-dark-400">加载中...</div>
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-dark-400 mb-4">还没有角色，创建一个开始聊天吧！</div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
            >
              创建第一个角色
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {characters.map((character) => {
              const state = characterStates[character.id]
              return (
                <CharacterCard
                  key={character.id}
                  character={character}
                  favorability={state?.favorability || 0}
                  daysTogether={playCreateStorageService.calculateDaysTogether(state?.firstChatTime)}
                  lastChatTime={state?.lastChatTime}
                  onClick={() => {
                    clearOptions() // 清除旧的对话选项
                    selectCharacter(character)
                    navigate(`/play-create/chat/${character.id}`)
                  }}
                  onViewProfile={handleViewProfile}
                  onDelete={handleDeleteClick}
                />
              )
            })}
          </div>
        )}
      </div>

      {/* 创建角色模态框 */}
      {showCreateModal && !showImageGenerator && (
        <CreateCharacterModal
          onClose={() => setShowCreateModal(false)}
          onCreated={async (character) => {
            setCreatedCharacter(character)
            setShowCreateModal(false)
            setShowImageGenerator(true)
          }}
        />
      )}

      {/* 图片生成器 */}
      {showImageGenerator && createdCharacter && (
        <CharacterImageGenerator
          character={createdCharacter}
          onClose={() => {
            console.log('❌ [onClose] 用户取消图片生成')
            setShowImageGenerator(false)
            setCreatedCharacter(null)
            // 用户取消了，不保存角色
            console.log('⏭️ [onClose] 取消创建，不保存')
          }}
          onImagesGenerated={async (images) => {
            console.log('📸 图片生成回调触发, images:', images)
            
            // 更新角色的图片
            const updatedCharacter = {
              ...createdCharacter,
              avatar: images.avatar,
              background: images.background
            }
            console.log('💾 准备保存角色档案:', {
              id: updatedCharacter.id,
              name: updatedCharacter.name,
              hasAvatar: !!updatedCharacter.avatar,
              hasBackground: !!updatedCharacter.background,
              avatarUrl: updatedCharacter.avatar,
              backgroundUrl: updatedCharacter.background
            })
            
            await playCreateStorageService.saveCharacterProfile(updatedCharacter)
            console.log('✅ 角色档案已保存到数据库')
            
            setShowImageGenerator(false)
            setCreatedCharacter(null)
            await loadCharacters()
            
            // 直接进入对话窗口
            console.log('🚀 选择角色并导航到聊天窗口:', updatedCharacter.id)
            clearOptions() // 清除旧的对话选项
            selectCharacter(updatedCharacter)
            navigate(`/play-create/chat/${updatedCharacter.id}`)
            toast.success('角色创建成功！')
          }}
        />
      )}

      {/* 角色档案查看模态框 */}
      {showProfileModal && selectedCharacterForProfile && (
        <CharacterProfileModal
          character={selectedCharacterForProfile}
          onClose={() => {
            setShowProfileModal(false)
            setSelectedCharacterForProfile(null)
          }}
        />
      )}

      {/* 删除确认对话框 */}
      {showDeleteConfirm && characterToDelete && (
        <DeleteConfirmDialog
          character={characterToDelete}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  )
}

// 删除确认对话框组件
const DeleteConfirmDialog = ({ character, isDeleting, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg p-6 w-full max-w-md border border-dark-600">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle size={24} className="text-red-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">确认删除</h3>
            <p className="text-sm text-dark-400">此操作无法撤销</p>
          </div>
        </div>

        <div className="bg-dark-700 rounded-lg p-4 mb-6">
          <p className="text-white mb-2">
            确定要删除角色 <span className="font-bold text-primary">{character.name}</span> 吗？
          </p>
          <p className="text-sm text-dark-300">
            删除后将清除所有相关数据，包括：
          </p>
          <ul className="text-sm text-dark-400 mt-2 space-y-1 list-disc list-inside">
            <li>角色档案和设定</li>
            <li>所有聊天记录</li>
            <li>好感度和状态数据</li>
            <li>角色日记</li>
            <li>服装和表情</li>
          </ul>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
            disabled={isDeleting}
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isDeleting}
          >
            {isDeleting ? '删除中...' : '确认删除'}
          </button>
        </div>
      </div>
    </div>
  )
}

// 创建角色模态框组件
const CreateCharacterModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [personality, setPersonality] = useState('')
  const [enableAdultMode, setEnableAdultMode] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [optimizedData, setOptimizedData] = useState(null)
  const [showOptimized, setShowOptimized] = useState(false)

  // 从sessionStorage读取初始描述
  useEffect(() => {
    const initialDescription = sessionStorage.getItem('character-initial-description')
    if (initialDescription) {
      setDescription(initialDescription)
      sessionStorage.removeItem('character-initial-description')
    }
  }, [])

  // 优化人设
  const handleOptimizePersonality = async () => {
    if (!name.trim()) {
      toast.error('请先输入角色名称')
      return
    }

    try {
      setIsOptimizing(true)
      toast.loading('正在优化人设...', { id: 'optimizing' })
      
      const optimized = await personalityOptimizationService.optimizePersonality(
        name.trim(),
        description.trim(),
        personality.trim()
      )
      
      setOptimizedData(optimized)
      setShowOptimized(true)
      toast.success('人设优化完成！', { id: 'optimizing' })
    } catch (error) {
      console.error('❌ 优化人设失败:', error)
      toast.error('优化人设失败：' + error.message, { id: 'optimizing' })
    } finally {
      setIsOptimizing(false)
    }
  }

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('请输入角色名称')
      return
    }

    try {
      setIsCreating(true)
      
      // 使用优化后的人设（如果有），否则使用用户输入的
      const finalDescription = optimizedData?.optimizedDescription || description.trim() || '一个有趣的AI角色'
      const finalPersonality = optimizedData?.optimizedPersonality || personality.trim() || '友好、开朗'
      const finalSystemPrompt = optimizedData?.systemPrompt || `你是一个名为${name.trim()}的虚拟角色。性格特点：${finalPersonality}。${finalDescription ? `背景：${finalDescription}` : ''}`
      
      // 生成标签
      const TagGeneratorService = (await import('../services/tagGeneratorService')).default
      let tags = []
      try {
        tags = await TagGeneratorService.generateCharacterTags({
          name: name.trim(),
          description: finalDescription,
          personality: finalPersonality,
          background: finalDescription,
          interests: optimizedData?.interests || []
        })
        console.log('🏷️ 已生成角色标签:', tags)
      } catch (error) {
        console.warn('⚠️ 标签生成失败，使用默认标签:', error)
        tags = ['原创', '推荐']
      }
      
      const character = {
        id: `char-${Date.now()}`,
        name: name.trim(),
        description: finalDescription,
        personality: finalPersonality,
        avatar: '',
        background: '',
        systemPrompt: finalSystemPrompt,
        optimizedSystemPrompt: optimizedData?.systemPrompt,
        optimizedPersonality: optimizedData,
        keyTraits: optimizedData?.keyTraits || [],
        interests: optimizedData?.interests || [],
        speechStyle: optimizedData?.speechStyle || '自然、友好',
        tags,
        enableAdultMode: enableAdultMode,  // 添加NSFW开关状态
        status: 'draft', // 默认状态为草稿
        isPublic: false // 默认未发布
      }

      // 先保存角色基本信息
      await playCreateStorageService.saveCharacterProfile(character)
      
      // 初始化角色状态
      await playCreateStorageService.saveCharacterState(character.id, {
        favorability: 0,
        firstChatTime: Date.now(),
        lastChatTime: Date.now(),
        totalChatCount: 0,
        currentLevel: '陌生'
      })

      // 打开图片生成页面
      setIsCreating(false)
      onCreated(character) // 传递角色数据给父组件
    } catch (error) {
      console.error('❌ 创建角色失败:', error)
      toast.error('创建角色失败：' + error.message, { id: 'creating-character' })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg p-6 w-full max-w-lg max-h-[95vh] overflow-y-auto border border-dark-600">
        <h2 className="text-2xl font-bold text-white mb-4">创建新角色</h2>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              角色名称 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="输入角色名称"
              className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              角色描述
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="描述这个角色的背景和特点"
              rows={2}
              className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              性格特点
            </label>
            <input
              type="text"
              value={personality}
              onChange={(e) => setPersonality(e.target.value)}
              placeholder="例如：友好、开朗、幽默"
              className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* NSFW开关 */}
          <div className="flex items-center justify-between p-3 bg-dark-700 rounded-lg border border-dark-600">
            <div className="flex-1">
              <label className="text-sm font-medium text-white flex items-center gap-2">
                <span className="text-lg">🔞</span>
                NSFW模式
              </label>
              <p className="text-xs text-dark-400 mt-0.5">
                启用后，允许成人内容对话（18+）
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEnableAdultMode(!enableAdultMode)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                enableAdultMode ? 'bg-primary' : 'bg-dark-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enableAdultMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* AI优化人设按钮 */}
          {(description.trim() || personality.trim()) && (
            <div>
              <button
                onClick={handleOptimizePersonality}
                disabled={isOptimizing || !name.trim()}
                className="w-full px-4 py-2 bg-secondary hover:bg-secondary/80 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Sparkles size={16} />
                {isOptimizing ? '优化中...' : 'AI优化人设'}
              </button>
            </div>
          )}

          {/* 显示优化后的人设预览 */}
          {showOptimized && optimizedData && (
            <div className="mt-4 p-4 bg-dark-700 rounded-lg border border-primary/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-primary">优化后的人设预览</span>
                <button
                  onClick={() => {
                    setShowOptimized(false)
                    setOptimizedData(null)
                  }}
                  className="text-dark-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-dark-300">优化描述：</span>
                  <p className="text-white mt-1">{optimizedData.optimizedDescription}</p>
                </div>
                <div>
                  <span className="text-dark-300">优化性格：</span>
                  <p className="text-white mt-1">{optimizedData.optimizedPersonality}</p>
                </div>
                {optimizedData.keyTraits && optimizedData.keyTraits.length > 0 && (
                  <div>
                    <span className="text-dark-300">关键特征：</span>
                    <p className="text-white mt-1">{optimizedData.keyTraits.join('、')}</p>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setDescription(optimizedData.optimizedDescription)
                  setPersonality(optimizedData.optimizedPersonality)
                }}
                className="mt-3 w-full px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded text-xs transition-colors"
              >
                应用优化结果
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
            disabled={isCreating}
          >
            取消
          </button>
          <button
            onClick={handleCreate}
            className="flex-1 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors"
            disabled={isCreating || !name.trim()}
          >
            {isCreating ? '创建中...' : '创建'}
          </button>
        </div>
      </div>
    </div>
  )
}
